import { canChangeApplicationSetting } from '@/api/core/authorization';
import type { CatalogPresentation, PropertyCatalog } from '@/api/core/properties';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { ZoomableImg } from '@/components/zoomable-img';
import { formatDateTimeWithOffset } from '@/lib/format';
import { safeImageUri } from '@/lib/safe-image-uri';
import { isUuid } from '@/lib/validation';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  DetailPage,
  DetailRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@mgrvtechgroup/admin-ds';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useInsertionEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApplicationSettingForm } from './application-setting-form';
import { photoCaption } from './media/media-presentation';
import { PropertyMediaSection, type MediaPatch } from './media/property-media-section';
import { NativeRoomSalesSection } from './native-room-sales-section';
import { PHOTO_PREVIEW_LIMIT, PhotoToggle } from './photo-toggle';
import { LegacyPresentationSection } from './presentation/legacy-presentation-section';
import {
  PropertyDetailSkeleton,
  PropertyFailure,
  PropertyInvalidAddress,
  RoomCatalogSkeleton,
} from './property-feedback';
import {
  propertyApplicationSettingMutationOptions,
  propertyCatalogPageQuery,
  propertyCatalogQuery,
  propertyRuntime,
  providerIntegrationStateQuery,
  readCurrentProviderLinkedData,
  readCurrentProviderLiveConnection,
  websitePublicationQuery,
  type PropertyRuntime,
} from './property-queries';
import { PROPERTY_MEDIA_PARAMS, propertyDetailState, withPropertyMedia } from './property-url';
import { ProviderIntegrationStateSection } from './provider-integration-state-section';
import { ProviderLinkedDataSection } from './provider-linked-data-section';
import { ProviderLiveConnectionSection } from './provider-live-connection-section';
import {
  emptyRecommendationWeightOwnerState,
  RecommendationWeightSection,
  type RecommendationWeightOwnerState,
} from './recommendation-weight-section';
import { roomSalesLocalStateKey } from './room-sales-queries';
import {
  emptyInitialRoomTypeBindingOwnerState,
  InitialRoomTypeBindingSection,
  type InitialRoomTypeBindingOwnerState,
} from './rooms/initial-room-type-binding-section';
import { RoomCatalogSection } from './rooms/room-catalog-section';
import { observeUnitClassificationParentOwner } from './rooms/unit-sale-classification-section';
import { WebsitePublicationSection } from './website-publication-section';

const PROPERTY_DETAIL_TABS = [
  { value: 'basic', label: '기본 정보' },
  { value: 'integrations', label: '외부 연동' },
  { value: 'web-preview', label: '웹 콘텐츠' },
  { value: 'space', label: '객실' },
  { value: 'media', label: '사진·영상' },
] as const;
type PropertyDetailTab = (typeof PROPERTY_DETAIL_TABS)[number]['value'];
type RoomSortField = 'room_no' | 'floor';
interface PendingRoomSortFocus {
  propertyId: string;
  apiNamespace: string;
  session: PropertyRuntime['session'];
  generation: number;
  status: string;
  selectedTab: PropertyDetailTab;
  sortBy: string | null;
  sortDirection: string | null;
  requestIdentity: string;
  field: RoomSortField;
}

interface RecommendationScopeCommitStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => string | null;
  commit: (scopeKey: string) => void;
}

function createRecommendationScopeCommitStore(): RecommendationScopeCommitStore {
  let committedScopeKey: string | null = null;
  const listeners = new Set<() => void>();
  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => committedScopeKey,
    commit: (scopeKey) => {
      if (committedScopeKey === scopeKey) return;
      committedScopeKey = scopeKey;
      listeners.forEach((listener) => {
        listener();
      });
    },
  };
}
function roomSortRequestIdentity(
  propertyId: string,
  apiNamespace: string,
  generation: number,
  selectedTab: PropertyDetailTab,
  sortBy: string | null,
  sortDirection: string | null,
  roomCursor: string | null,
  roomSortCursor: string | null,
) {
  return JSON.stringify([
    propertyId,
    apiNamespace,
    generation,
    selectedTab,
    sortBy,
    sortDirection,
    roomCursor,
    roomSortCursor,
  ]);
}
const activeTab = (value: string | null): PropertyDetailTab =>
  PROPERTY_DETAIL_TABS.find((tab) => tab.value === value)?.value ?? 'basic';
function currentPropertyPresentation(
  catalog: PropertyCatalog | undefined,
): CatalogPresentation | null {
  const propertyId = catalog?.property.property_id;
  if (!propertyId) return null;
  return (
    catalog.presentations.find(
      (presentation) =>
        presentation.targetKind === 'PROPERTY' &&
        presentation.propertyId?.toLowerCase() === propertyId.toLowerCase(),
    ) ?? null
  );
}

function canonicalText(
  presentation: CatalogPresentation | null,
  purpose: string,
  languages: readonly string[] = ['ko', 'en'],
): string | null {
  if (!presentation) return null;
  for (const language of languages) {
    const values = presentation.texts
      .filter((item) => item.purpose === purpose && item.language === language)
      .map((item) => item.value?.trim() ?? '')
      .filter(Boolean);
    if (values.length === 1) return values[0] ?? null;
    if (values.length > 1) return null;
  }
  return null;
}

function canonicalPropertyPhotos(catalog: PropertyCatalog | undefined) {
  if (!catalog)
    return { photos: [] as { uri: string; itemId: string; label: string }[], unavailableCount: 0 };
  const groups = catalog.mediaGroups
    .filter(
      (group) =>
        group.ownerScopeKind === 'PROPERTY' &&
        group.ownerPropertyId.toLowerCase() === catalog.property.property_id.toLowerCase(),
    )
    .sort(
      (left, right) =>
        (left.sourceOrder ?? Number.MAX_SAFE_INTEGER) -
        (right.sourceOrder ?? Number.MAX_SAFE_INTEGER),
    );
  const result = {
    photos: [] as { uri: string; itemId: string; label: string }[],
    unavailableCount: 0,
  };
  for (const group of groups) {
    const items = catalog.mediaItems
      .filter((item) => item.groupId === group.id)
      .sort(
        (left, right) =>
          (left.sourceOrder ?? Number.MAX_SAFE_INTEGER) -
          (right.sourceOrder ?? Number.MAX_SAFE_INTEGER),
      );
    for (const item of items) {
      if (item.kind !== 'IMAGE') continue;
      const uri = safeImageUri(item.primaryUri ?? item.thumbnailUri);
      if (!uri) {
        result.unavailableCount += 1;
        continue;
      }
      result.photos.push({
        uri,
        itemId: item.id,
        label: photoCaption(group, item.contentSlot),
      });
    }
  }
  return result;
}

function facilityLabels(presentation: CatalogPresentation | null): string[] {
  const facilities = presentation?.features.facilities;
  return Array.isArray(facilities)
    ? facilities.filter(
        (value): value is string => typeof value === 'string' && Boolean(value.trim()),
      )
    : [];
}

const activePropertyBySession = new WeakMap<PropertyRuntime['session'], string>();
const providerSessionIdentity = new WeakMap<PropertyRuntime['session'], number>();
let nextProviderSessionIdentity = 1;
const propertyRuntimeIdentity = new WeakMap<PropertyRuntime, number>();
let nextPropertyRuntimeIdentity = 1;

function getProviderSessionIdentity(session: PropertyRuntime['session']) {
  let identity = providerSessionIdentity.get(session);
  if (identity === undefined) {
    identity = nextProviderSessionIdentity++;
    providerSessionIdentity.set(session, identity);
  }
  return identity;
}

function getPropertyRuntimeIdentity(runtime: PropertyRuntime) {
  let identity = propertyRuntimeIdentity.get(runtime);
  if (identity === undefined) {
    identity = nextPropertyRuntimeIdentity++;
    propertyRuntimeIdentity.set(runtime, identity);
  }
  return identity;
}

const providerRequestContextBySession = new WeakMap<
  PropertyRuntime['session'],
  {
    propertyId: string;
    apiNamespace: string;
    selectedTab: PropertyDetailTab;
    generation: number;
    status: string;
    environment: 'dev' | 'prod';
    branchId: number;
    version: number;
  }
>();

interface InitialBindingRequestOwner {
  token: object;
  scopeKey: string;
  epoch: number;
  request: { requestId: string; controller: AbortController } | null;
}
const initialBindingOwnerBySession = new WeakMap<
  PropertyRuntime['session'],
  InitialBindingRequestOwner
>();

export function PropertyDetailPage({
  runtime = propertyRuntime,
  enableServerSorting = false,
}: {
  runtime?: PropertyRuntime;
  enableServerSorting?: boolean;
}) {
  const { propertyId = '' } = useParams();
  const [unitCommandBoundary] = useState<object>(() => ({}));
  const currentPropertyId = useRef(propertyId);
  useLayoutEffect(() => {
    currentPropertyId.current = propertyId;
  }, [propertyId]);
  const [failedPropertyPhotoKeys, setFailedPropertyPhotoKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [photosExpanded, setPhotosExpanded] = useState(false);
  useEffect(() => {
    activePropertyBySession.set(runtime.session, propertyId);
    return () => {
      if (activePropertyBySession.get(runtime.session) === propertyId)
        activePropertyBySession.delete(runtime.session);
    };
  }, [runtime.session, propertyId]);
  const [params, setParams] = useSearchParams();
  const state = propertyDetailState(params);
  const selectedTab = activeTab(params.get('tab'));
  const previousTab = useRef<PropertyDetailTab>(selectedTab);
  useEffect(() => {
    if (previousTab.current === selectedTab) return;
    previousTab.current = selectedTab;
    // 패널 위쪽이 스크롤 상자 맨 위에 붙으면 본문 위 여백(py-6)이 사라져 헤더에 붙어 보인다 — 패널의 scroll-mt-6이 그 여백을 남긴다.
    const panel = document.getElementById(`property-detail-panel-${selectedTab}`);
    const scrollIntoView = panel && Reflect.get(panel, 'scrollIntoView');
    if (panel && typeof scrollIntoView === 'function')
      scrollIntoView.call(panel, { block: 'start' });
  }, [selectedTab]);
  const session = useOperatorSession(runtime.session);
  const queryClient = useQueryClient();
  const invalid =
    !state.valid ||
    !isUuid(propertyId) ||
    (!enableServerSorting &&
      (state.cursors.sortBy !== undefined || state.cursors.roomSort !== null));
  const propertyDetail = useQuery(
    propertyCatalogQuery(
      runtime,
      session.generation,
      propertyId,
      { room: null, roomSort: null },
      !invalid && session.status === 'authorized',
    ),
  );
  const detail = useQuery(
    propertyCatalogPageQuery(
      runtime,
      session.generation,
      propertyId,
      state.cursors,
      !invalid && session.status === 'authorized',
    ),
  );
  const propertyDenied = session.deniedDomains.includes('property');
  useInsertionEffect(() => {
    observeUnitClassificationParentOwner(unitCommandBoundary, {
      runtime,
      generation: session.generation,
      propertyId,
      branchId: propertyDetail.data?.property.branch_id ?? null,
      authorization: session.authorization,
      sessionStatus: session.status,
      propertyDenied,
      enabled: runtime.unitClassificationEnabled === true,
    });
  }, [
    unitCommandBoundary,
    runtime,
    runtime.session,
    runtime.apiNamespace,
    session.generation,
    propertyId,
    propertyDetail.data?.property.branch_id,
    session.authorization,
    session.status,
    propertyDenied,
    runtime.unitClassificationEnabled,
  ]);
  const bindingScopeKey = JSON.stringify([
    getProviderSessionIdentity(runtime.session),
    runtime.apiNamespace,
    session.generation,
    propertyId.toLowerCase(),
    propertyDetail.data?.property.branch_id ?? null,
    session.status,
    session.authorization,
    session.deniedDomains.includes('property'),
    runtime.initialTypeBindingEnabled === true,
  ]);
  const [bindingOwnerToken] = useState<object>(() => ({}));
  const [bindingOwner, setBindingOwner] = useState(() => ({
    scopeKey: bindingScopeKey,
    epoch: 0,
    value: emptyInitialRoomTypeBindingOwnerState,
  }));
  let bindingRequestOwner = initialBindingOwnerBySession.get(runtime.session);
  if (
    bindingRequestOwner?.token !== bindingOwnerToken ||
    bindingRequestOwner.scopeKey !== bindingScopeKey
  ) {
    bindingRequestOwner?.request?.controller.abort();
    bindingRequestOwner = {
      token: bindingOwnerToken,
      scopeKey: bindingScopeKey,
      epoch: (bindingRequestOwner?.epoch ?? -1) + 1,
      request: null,
    };
    initialBindingOwnerBySession.set(runtime.session, bindingRequestOwner);
  }
  if (bindingOwner.scopeKey !== bindingScopeKey) {
    setBindingOwner({
      scopeKey: bindingScopeKey,
      epoch: bindingRequestOwner.epoch,
      value: emptyInitialRoomTypeBindingOwnerState,
    });
  }
  const bindingEpoch = bindingRequestOwner.epoch;
  const bindingOwnerState =
    bindingOwner.scopeKey === bindingScopeKey
      ? bindingOwner.value
      : emptyInitialRoomTypeBindingOwnerState;
  const setBindingOwnerState: Dispatch<SetStateAction<InitialRoomTypeBindingOwnerState>> = (
    update,
  ) => {
    setBindingOwner((current) => {
      if (current.scopeKey !== bindingScopeKey || current.epoch !== bindingEpoch) return current;
      const value = typeof update === 'function' ? update(current.value) : update;
      return { ...current, value };
    });
  };
  const claimBindingRequest = (
    epoch: number,
    requestId: string,
    kind: 'review' | 'write' | 'replay',
  ) => {
    const owner = initialBindingOwnerBySession.get(runtime.session);
    if (
      owner?.token !== bindingOwnerToken ||
      owner.scopeKey !== bindingScopeKey ||
      owner.epoch !== epoch ||
      owner.request !== null
    )
      return false;
    owner.request = { requestId, controller: new AbortController() };
    setBindingOwner((current) =>
      current.scopeKey !== bindingScopeKey ||
      current.epoch !== epoch ||
      current.value.pendingRequestId !== null
        ? current
        : {
            ...current,
            value: { ...current.value, pendingRequestId: requestId, pendingKind: kind },
          },
    );
    return true;
  };
  const isBindingRequestCurrent = (epoch: number, requestId: string) => {
    const owner = initialBindingOwnerBySession.get(runtime.session);
    return (
      owner?.token === bindingOwnerToken &&
      owner.scopeKey === bindingScopeKey &&
      owner.epoch === epoch &&
      owner.request?.requestId === requestId
    );
  };
  const getBindingRequestSignal = (epoch: number, requestId: string) => {
    const owner = initialBindingOwnerBySession.get(runtime.session);
    return isBindingRequestCurrent(epoch, requestId)
      ? owner?.request?.controller.signal
      : undefined;
  };
  const finishBindingRequest = (epoch: number, requestId: string) => {
    const owner = initialBindingOwnerBySession.get(runtime.session);
    if (!isBindingRequestCurrent(epoch, requestId) || !owner) return;
    owner.request = null;
    setBindingOwner((current) =>
      current.scopeKey !== bindingScopeKey ||
      current.epoch !== epoch ||
      current.value.pendingRequestId !== requestId
        ? current
        : { ...current, value: { ...current.value, pendingRequestId: null, pendingKind: null } },
    );
  };
  useEffect(
    () => () => {
      const owner = initialBindingOwnerBySession.get(runtime.session);
      if (owner?.token !== bindingOwnerToken) return;
      owner.request?.controller.abort();
      initialBindingOwnerBySession.delete(runtime.session);
    },
    [runtime.session, bindingOwnerToken],
  );
  const pendingRoomSortFocus = useRef<PendingRoomSortFocus | null>(null);
  useEffect(() => {
    const intent = pendingRoomSortFocus.current;
    if (!intent) return;
    const currentSortBy = state.cursors.sortBy ?? null;
    const currentSortDirection = state.cursors.sortDirection ?? null;
    const currentRequestIdentity = roomSortRequestIdentity(
      propertyId,
      runtime.apiNamespace,
      session.generation,
      selectedTab,
      currentSortBy,
      currentSortDirection,
      state.cursors.room,
      state.cursors.roomSort,
    );
    if (
      intent.propertyId !== propertyId ||
      intent.apiNamespace !== runtime.apiNamespace ||
      intent.session !== runtime.session ||
      intent.generation !== session.generation ||
      intent.status !== session.status ||
      session.status !== 'authorized' ||
      intent.selectedTab !== selectedTab ||
      selectedTab !== 'space' ||
      intent.sortBy !== currentSortBy ||
      intent.sortDirection !== currentSortDirection ||
      intent.requestIdentity !== currentRequestIdentity
    ) {
      pendingRoomSortFocus.current = null;
      return;
    }
    if (detail.isError) {
      pendingRoomSortFocus.current = null;
      return;
    }
    if (!detail.data) return;
    pendingRoomSortFocus.current = null;
    if (document.activeElement !== document.body) return;
    if (detail.data.property.property_id.toLowerCase() !== propertyId.toLowerCase()) return;
    const panel = document.getElementById('property-detail-panel-space');
    const expectedHeader = intent.field === 'room_no' ? '호실' : '층';
    const target = [
      ...(panel?.querySelectorAll<HTMLElement>('[aria-sort] button, button[aria-sort]') ?? []),
    ].find((button) => button.textContent.trim() === expectedHeader);
    target?.focus();
  }, [
    detail.data,
    detail.isError,
    propertyId,
    runtime.apiNamespace,
    runtime.session,
    selectedTab,
    session.generation,
    state.cursors.sortBy,
    state.cursors.sortDirection,
    state.cursors.room,
    state.cursors.roomSort,
    session.status,
  ]);
  const property = propertyDetail.data?.property;
  const apiNamespaceAtRender = runtime.apiNamespace;
  const recommendationScopeKey = JSON.stringify([
    getPropertyRuntimeIdentity(runtime),
    getProviderSessionIdentity(runtime.session),
    apiNamespaceAtRender,
    session.generation,
    session.status,
    session.authorization,
    session.deniedDomains.includes('property'),
    propertyId.toLowerCase(),
    property?.branch_id ?? null,
    runtime.recommendationWeightEnabled === true,
  ]);
  const [recommendationOwner, setRecommendationOwner] = useState(() => ({
    scopeKey: recommendationScopeKey,
    epoch: 0,
    value: emptyRecommendationWeightOwnerState,
  }));
  const recommendationEpoch =
    recommendationOwner.scopeKey === recommendationScopeKey
      ? recommendationOwner.epoch
      : recommendationOwner.epoch + 1;
  if (recommendationOwner.scopeKey !== recommendationScopeKey) {
    setRecommendationOwner({
      scopeKey: recommendationScopeKey,
      epoch: recommendationEpoch,
      value: emptyRecommendationWeightOwnerState,
    });
  }
  const recommendationCurrentScopeRef = useRef<{
    scopeKey: string;
    epoch: number;
  } | null>(null);
  const recommendationCommitKey = JSON.stringify([recommendationScopeKey, recommendationEpoch]);
  const recommendationScopeCommitStoreRef = useRef<RecommendationScopeCommitStore | null>(null);
  recommendationScopeCommitStoreRef.current ??= createRecommendationScopeCommitStore();
  const recommendationScopeCommitStore = recommendationScopeCommitStoreRef.current;
  const committedRecommendationScopeKey = useSyncExternalStore(
    recommendationScopeCommitStore.subscribe,
    recommendationScopeCommitStore.getSnapshot,
    recommendationScopeCommitStore.getSnapshot,
  );
  useLayoutEffect(() => {
    recommendationCurrentScopeRef.current = {
      scopeKey: recommendationScopeKey,
      epoch: recommendationEpoch,
    };
    recommendationScopeCommitStore.commit(recommendationCommitKey);
  }, [
    recommendationCommitKey,
    recommendationScopeKey,
    recommendationEpoch,
    recommendationScopeCommitStore,
  ]);
  const recommendationOwnerState =
    recommendationOwner.scopeKey === recommendationScopeKey &&
    recommendationOwner.epoch === recommendationEpoch
      ? recommendationOwner.value
      : emptyRecommendationWeightOwnerState;
  const setRecommendationOwnerState: Dispatch<SetStateAction<RecommendationWeightOwnerState>> = (
    update,
  ) => {
    setRecommendationOwner((current) => {
      if (current.scopeKey !== recommendationScopeKey || current.epoch !== recommendationEpoch)
        return current;
      const value = typeof update === 'function' ? update(current.value) : update;
      return { ...current, value };
    });
  };
  const isRecommendationScopeCurrent = useCallback(() => {
    const liveSession = runtime.session.getSnapshot();
    const currentScope = recommendationCurrentScopeRef.current;
    return (
      committedRecommendationScopeKey === recommendationCommitKey &&
      currentScope?.scopeKey === recommendationScopeKey &&
      currentScope.epoch === recommendationEpoch &&
      runtime.apiNamespace === apiNamespaceAtRender &&
      liveSession.generation === session.generation &&
      liveSession.status === session.status &&
      JSON.stringify(liveSession.authorization) === JSON.stringify(session.authorization) &&
      runtime.recommendationWeightEnabled === true &&
      currentPropertyId.current === propertyId &&
      activePropertyBySession.get(runtime.session) === propertyId
    );
  }, [
    apiNamespaceAtRender,
    committedRecommendationScopeKey,
    propertyId,
    recommendationCommitKey,
    recommendationEpoch,
    recommendationScopeKey,
    runtime,
    session.authorization,
    session.generation,
    session.status,
  ]);
  const providerBinding = {
    propertyId,
    environment: property?.environment ?? 'dev',
    branchId: property?.branch_id ?? 0,
    version: property?.version ?? 0,
  } as const;
  const providerLinkedDataScopeKey = JSON.stringify([
    getProviderSessionIdentity(runtime.session),
    runtime.apiNamespace,
    session.generation,
    session.status,
    propertyId,
    providerBinding.environment,
    providerBinding.branchId,
    providerBinding.version,
    selectedTab,
  ]);
  const isProviderContextCurrent = () => {
    const current = providerRequestContextBySession.get(runtime.session);
    const liveSession = runtime.session.getSnapshot();
    return (
      current?.selectedTab === 'integrations' &&
      current.propertyId === providerBinding.propertyId &&
      current.apiNamespace === runtime.apiNamespace &&
      current.generation === liveSession.generation &&
      current.status === 'authorized' &&
      liveSession.generation === current.generation &&
      liveSession.status === 'authorized' &&
      current.environment === providerBinding.environment &&
      current.branchId === providerBinding.branchId &&
      current.version === providerBinding.version &&
      activePropertyBySession.get(runtime.session) === propertyId
    );
  };
  useLayoutEffect(() => {
    const context = {
      propertyId,
      apiNamespace: runtime.apiNamespace,
      selectedTab,
      generation: session.generation,
      status: session.status,
      environment: providerBinding.environment,
      branchId: providerBinding.branchId,
      version: providerBinding.version,
    };
    providerRequestContextBySession.set(runtime.session, context);
    return () => {
      if (providerRequestContextBySession.get(runtime.session) === context)
        providerRequestContextBySession.delete(runtime.session);
    };
  }, [
    runtime.session,
    propertyId,
    runtime.apiNamespace,
    selectedTab,
    session.generation,
    session.status,
    providerBinding.environment,
    providerBinding.branchId,
    providerBinding.version,
  ]);
  const websitePublication = useQuery(
    websitePublicationQuery(
      runtime,
      session.generation,
      {
        propertyId,
        branchId: property?.branch_id ?? 0,
        environment: property?.environment ?? 'dev',
        version: property?.version ?? 0,
      },
      !invalid &&
        selectedTab === 'web-preview' &&
        session.status === 'authorized' &&
        property !== undefined,
      () => activePropertyBySession.get(runtime.session) === propertyId,
    ),
  );
  const providerIntegrationState = useQuery(
    providerIntegrationStateQuery(
      runtime,
      session.generation,
      providerBinding,
      !invalid &&
        selectedTab === 'integrations' &&
        session.status === 'authorized' &&
        property !== undefined,
      isProviderContextCurrent,
    ),
  );
  const applicationSettingMutation = useMutation(
    propertyApplicationSettingMutationOptions(
      runtime,
      queryClient,
      session.generation,
      propertyId,
      () => activePropertyBySession.get(runtime.session) === propertyId,
    ),
  );
  if (session.status !== 'authorized') return null;
  const propertyPresentation = currentPropertyPresentation(propertyDetail.data);
  const propertyPhotoResult = propertyDetail.isError
    ? { photos: [], unavailableCount: 0 }
    : canonicalPropertyPhotos(propertyDetail.data);
  const propertyPhotos = propertyPhotoResult.photos;
  const shownPropertyPhotos = photosExpanded
    ? propertyPhotos
    : propertyPhotos.slice(0, PHOTO_PREVIEW_LIMIT);
  // 사진이 어떤 용도·분류로 저장됐는지는 사진·영상 탭이 그룹 단위로 보여 준다.
  const propertyMediaHref = `?${withPropertyMedia(params, {
    target: { kind: 'PROPERTY', id: null, floor: null },
  }).toString()}`;
  const facilityNames = facilityLabels(propertyPresentation);
  const missingFact = propertyDetail.isError ? '마지막으로 확인된 정보' : '정보 없음';
  const roomCountLabel = propertyDetail.data
    ? `${String(propertyDetail.data.totalRoomCount)}개`
    : null;
  const propertyFactGroups = [
    {
      title: '건물 및 위치',
      facts: [
        ['건물명', canonicalText(propertyPresentation, 'FULL_NAME') ?? property?.name ?? null],
        ['주소', canonicalText(propertyPresentation, 'ADDRESS')],
        [
          '우편번호',
          typeof propertyPresentation?.features.postal_code === 'string'
            ? propertyPresentation.features.postal_code
            : null,
        ],
        ['브랜드', canonicalText(propertyPresentation, 'BRAND_NAME')],
      ],
    },
    {
      title: '소개 및 안내',
      facts: [
        ['소개', canonicalText(propertyPresentation, 'DESCRIPTION')],
        ['주차 안내', canonicalText(propertyPresentation, 'PARKING_INFO')],
      ],
    },
  ] as const;
  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink render={<Link to={state.returnTo}>지점</Link>} />
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{property?.name ?? '지점 상세'}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
  const isPropertyCurrent = () =>
    currentPropertyId.current === propertyId &&
    activePropertyBySession.get(runtime.session) === propertyId;
  /** 대상·방향은 필터라 기록을 쌓지 않고, 그룹을 펼치고 닫는 것만 뒤로 가기로 되돌릴 수 있게 한다. */
  const onMediaChange = (patch: MediaPatch) => {
    setParams(withPropertyMedia(params, patch), { replace: patch.groupId === undefined });
  };
  const roomData = detail.data;
  const roomCatalogContent = detail.isError ? (
    <>
      <div className="flex items-center gap-3">
        <p role="status" className="body text-warning-foreground">
          객실 목록을 최신 상태로 불러오지 못했습니다.
        </p>
        <Button variant="outline" onClick={() => void detail.refetch()}>
          객실 다시 조회
        </Button>
      </div>
      {propertyDetail.data && (
        <InitialRoomTypeBindingSection
          page={propertyDetail.data}
          cursors={state.cursors}
          runtime={runtime}
          generation={session.generation}
          propertyId={propertyId}
          authorization={session.authorization}
          isPropertyCurrent={isPropertyCurrent}
          ownerState={bindingOwnerState}
          setOwnerState={setBindingOwnerState}
          ownerEpoch={bindingEpoch}
          claimOwnerRequest={claimBindingRequest}
          isOwnerRequestCurrent={isBindingRequestCurrent}
          getOwnerRequestSignal={getBindingRequestSignal}
          finishOwnerRequest={finishBindingRequest}
          catalogError
        />
      )}
    </>
  ) : roomData ? (
    <>
      <RoomCatalogSection
        ownerBoundary={unitCommandBoundary}
        page={roomData}
        visible={selectedTab === 'space'}
        cursors={state.cursors}
        fetching={detail.isFetching}
        pending={detail.isPlaceholderData}
        runtime={runtime}
        generation={session.generation}
        propertyId={propertyId}
        enableServerSorting={enableServerSorting}
        sort={
          state.cursors.sortBy && state.cursors.sortDirection
            ? `${state.cursors.sortBy},${state.cursors.sortDirection}`
            : null
        }
        onSortChange={(sort, field) => {
          const roomPanel = document.getElementById('property-detail-panel-space');
          const shouldRestoreFocus = Boolean(roomPanel?.contains(document.activeElement));
          const [sortBy = null, sortDirection = null] = sort?.split(',') ?? [];
          pendingRoomSortFocus.current = shouldRestoreFocus
            ? {
                propertyId,
                apiNamespace: runtime.apiNamespace,
                session: runtime.session,
                generation: session.generation,
                status: session.status,
                selectedTab,
                sortBy,
                sortDirection,
                requestIdentity: roomSortRequestIdentity(
                  propertyId,
                  runtime.apiNamespace,
                  session.generation,
                  selectedTab,
                  sortBy,
                  sortDirection,
                  null,
                  null,
                ),
                field,
              }
            : null;
          const next = new URLSearchParams(params);
          next.delete('room_cursor');
          next.delete('room_sort_cursor');
          if (sort) {
            const [sortBy, sortDirection] = sort.split(',');
            next.set('sort_by', sortBy ?? '');
            next.set('sort_direction', sortDirection ?? '');
          } else {
            next.delete('sort_by');
            next.delete('sort_direction');
          }
          setParams(next);
        }}
        isPropertyCurrent={isPropertyCurrent}
        authorization={session.authorization}
        mediaHref={(target) => `?${withPropertyMedia(params, { target }).toString()}`}
        onCursor={(key, value) => {
          const param = key === 'room' ? 'room_cursor' : 'room_sort_cursor';
          const next = new URLSearchParams(params);
          if (value) next.set(param, value);
          else next.delete(param);
          setParams(next);
        }}
      />
      <InitialRoomTypeBindingSection
        page={roomData}
        cursors={state.cursors}
        runtime={runtime}
        generation={session.generation}
        propertyId={propertyId}
        authorization={session.authorization}
        isPropertyCurrent={isPropertyCurrent}
        ownerState={bindingOwnerState}
        setOwnerState={setBindingOwnerState}
        ownerEpoch={bindingEpoch}
        claimOwnerRequest={claimBindingRequest}
        isOwnerRequestCurrent={isBindingRequestCurrent}
        getOwnerRequestSignal={getBindingRequestSignal}
        finishOwnerRequest={finishBindingRequest}
      />
      <NativeRoomSalesSection
        key={roomSalesLocalStateKey(runtime, session.generation, propertyId)}
        runtime={runtime}
        generation={session.generation}
        propertyId={propertyId}
        isCurrent={() => currentPropertyId.current === propertyId}
      />
    </>
  ) : (
    <RoomCatalogSkeleton />
  );
  return (
    <Tabs
      className="min-h-0 flex-1"
      value={selectedTab}
      onValueChange={(value) => {
        const nextTab = PROPERTY_DETAIL_TABS.find((tab) => tab.value === value)?.value;
        if (!nextTab) return;
        const next = new URLSearchParams(params);
        if (nextTab === 'basic') next.delete('tab');
        else next.set('tab', nextTab);
        // 사진·영상의 대상·정렬·펼친 그룹은 그 탭의 상태다 — 다른 탭 주소와 이관 결과의 복귀 주소(returnTo)에 남기지 않는다.
        // (객실 목록·이관 이력 cursor는 숨겨 둔 패널이 상태를 이어 가도록 탭을 오가도 둔다.)
        if (nextTab !== 'media') for (const key of PROPERTY_MEDIA_PARAMS) next.delete(key);
        setParams(next);
      }}
    >
      {/* 스크롤하는 쪽은 DetailPage다. 본문 상자(Body)에 min-h-0을 주면 상자가 화면 높이로 눌려 내용이 밖으로 넘치고 아래 여백(py-6)이 사라진다. */}
      <DetailPage className="min-h-0 flex-1 overflow-y-auto">
        <ShellHeader>
          {property ? (
            <DetailPage.Header
              breadcrumb={breadcrumb}
              title={property.name}
              titleBadge={
                <div className="flex items-center gap-1.5">
                  <Badge tone={property.environment === 'prod' ? 'warning' : 'neutral'}>
                    {property.environment === 'prod' ? '운영' : '개발'}
                  </Badge>
                  <Badge tone={property.application_enabled ? 'success' : 'neutral'}>
                    {property.application_enabled ? '신청 허용' : '신청 차단'}
                  </Badge>
                </div>
              }
              description={property.code}
              actions={
                // 좁은 화면에서는 "투숙 현황"만 보여 제목과 한 줄에 둔다. 읽는 이름은 그대로 "이 지점 투숙 현황"이다.
                <Button
                  size="sm"
                  variant="outline"
                  nativeButton={false}
                  role="link"
                  aria-label="이 지점 투숙 현황"
                  render={
                    <Link to={`/stays?property_id=${encodeURIComponent(property.property_id)}`} />
                  }
                >
                  <span className="max-sm:hidden">이 지점 </span>투숙 현황
                </Button>
              }
            />
          ) : (
            <DetailPage.Header breadcrumb={breadcrumb} title="지점 상세" description={'\u00A0'} />
          )}
          {/*
            탭이 많아 좁은 폭에서는 가로로 스크롤한다. 스크롤하는 상자는 안에 든 것을 자르므로, 목록이 직접 스크롤하면
            탭 글자가 헤더 아래 선에 붙고 포커스 링(바깥 4px)과 활성 밑줄(목록 아래 선에 1px 겹침)이 잘린다.
            그래서 스크롤은 바깥 상자가 맡고, 목록은 위 여백(pt-2)을, 바깥 상자는 링이 들어갈 아래 여백(pb-1)을 둔다.
          */}
          <div className="overflow-x-auto pb-1">
            <TabsList
              className="w-max min-w-full px-[var(--layout-gutter)] pt-2"
              aria-label="지점 정보 구분"
            >
              {PROPERTY_DETAIL_TABS.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  id={`property-detail-tab-${tab.value}`}
                  aria-controls={
                    property && (selectedTab === tab.value || tab.value === 'space')
                      ? `property-detail-panel-${tab.value}`
                      : undefined
                  }
                  className="shrink-0"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </ShellHeader>
        {/*
          우측 속성 패널(aside)은 두지 않는다. DetailPage.Body는 aside가 있으면 본문 상자를 max-w-7xl 2단으로, 없으면
          max-w-5xl 한 칸으로 그려서, 기본정보에만 두면 탭을 오갈 때 본문이 가로로 튀고(1728px 화면에서 128px) 좁은
          화면에서는 맨 아래로 밀린다. 운영 정보는 aside 대신 기본정보 본문의 "운영 정보" 섹션에 둔다.
        */}
        <DetailPage.Body>
          {invalid ? (
            <PropertyInvalidAddress to={state.returnTo} label="지점 목록에서 다시 선택" />
          ) : propertyDetail.isError && !property ? (
            <PropertyFailure
              error={propertyDetail.error}
              onRetry={() => {
                void propertyDetail.refetch();
              }}
            />
          ) : property ? (
            <>
              {propertyDetail.isError && (
                <div className="flex flex-col items-start gap-2">
                  <p role="alert" className="body text-warning-foreground">
                    마지막으로 확인된 지점 정보입니다. 최신 정보를 불러오지 못했습니다.
                  </p>
                  <Button variant="outline" onClick={() => void propertyDetail.refetch()}>
                    최신 지점 정보 다시 조회
                  </Button>
                </div>
              )}
              <div
                id="property-detail-panel-basic"
                role="tabpanel"
                className="scroll-mt-6"
                aria-labelledby="property-detail-tab-basic"
                tabIndex={0}
                hidden={selectedTab !== 'basic'}
              >
                {selectedTab === 'basic' && (
                  <div className="flex min-w-0 flex-col gap-6">
                    <>
                      {propertyDetail.data && (
                        <LegacyPresentationSection
                          runtime={runtime}
                          catalog={detail.data ?? propertyDetail.data}
                        />
                      )}
                    </>
                    <div className="flex min-w-0 flex-col gap-6">
                      {propertyFactGroups.map(({ title, facts }) => (
                        <DetailPage.Section key={title} title={title} aria-label={title}>
                          <div className="flex flex-col gap-3">
                            {facts.map(([label, value]) => (
                              <DetailRow
                                key={label}
                                label={label}
                                value={
                                  value ?? (
                                    <span className="text-muted-foreground">{missingFact}</span>
                                  )
                                }
                              />
                            ))}
                          </div>
                        </DetailPage.Section>
                      ))}
                      <DetailPage.Section title="운영 정보" aria-label="운영 정보">
                        <div className="flex flex-col gap-3">
                          <DetailRow
                            label="숙박 신청"
                            value={property.application_enabled ? '허용' : '차단'}
                          />
                          <DetailRow
                            label="객실 수"
                            value={
                              roomCountLabel ?? (
                                <span className="text-muted-foreground">{missingFact}</span>
                              )
                            }
                          />
                          <DetailRow
                            label="환경"
                            value={property.environment === 'prod' ? '운영' : '개발'}
                          />
                          <DetailRow label="코드" value={property.code} />
                          <details>
                            <summary className="cursor-pointer">지점 식별 정보</summary>
                            <DetailRow label="기존 ID" value={String(property.branch_id)} />
                            <div className="w-fit">
                              <DetailRow label="통합 ID" value={property.property_id} copyable />
                            </div>
                          </details>
                          <DetailRow
                            label="등록 시각"
                            value={formatDateTimeWithOffset(property.created_at)}
                          />
                        </div>
                      </DetailPage.Section>
                      {(propertyPhotos.length > 0 ||
                        propertyPhotoResult.unavailableCount > 0 ||
                        !propertyDetail.isError) && (
                        <DetailPage.Section
                          title="지점 사진"
                          aria-label="지점 사진"
                          description={
                            propertyPhotos.length > 0
                              ? `${String(propertyPhotos.length)}장`
                              : undefined
                          }
                          actions={
                            <Link
                              className="body text-primary underline-offset-4 hover:underline"
                              to={propertyMediaHref}
                            >
                              사진·영상에서 보기
                            </Link>
                          }
                        >
                          {propertyPhotos.length > 0 ? (
                            <>
                              <ul className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                {shownPropertyPhotos.map((photo) => (
                                  <li key={photo.itemId}>
                                    <div className="bg-muted/40 flex aspect-square items-center justify-center overflow-hidden rounded-md border">
                                      {failedPropertyPhotoKeys.has(
                                        `${photo.itemId}\u0000${photo.uri}`,
                                      ) ? (
                                        <p
                                          role="status"
                                          className="body text-warning-foreground p-3 text-center"
                                        >
                                          사진을 표시할 수 없습니다.
                                        </p>
                                      ) : (
                                        <ZoomableImg
                                          buttonClassName="size-full"
                                          src={photo.uri}
                                          alt={`${property.name} ${photo.label}`}
                                          loading="lazy"
                                          decoding="async"
                                          onError={() => {
                                            setFailedPropertyPhotoKeys((current) =>
                                              new Set(current).add(
                                                `${photo.itemId}\u0000${photo.uri}`,
                                              ),
                                            );
                                          }}
                                          className="size-full object-contain"
                                        />
                                      )}
                                    </div>
                                    <p className="caption text-muted-foreground mt-1">
                                      {photo.label}
                                    </p>
                                  </li>
                                ))}
                              </ul>
                              <PhotoToggle
                                subject="지점 사진"
                                total={propertyPhotos.length}
                                expanded={photosExpanded}
                                onExpand={() => {
                                  setPhotosExpanded(true);
                                }}
                              />
                            </>
                          ) : propertyPhotoResult.unavailableCount > 0 ? (
                            <p role="status" className="body text-warning-foreground">
                              사진을 표시할 수 없습니다.
                            </p>
                          ) : (
                            <p className="body text-muted-foreground">등록된 사진이 없습니다.</p>
                          )}
                        </DetailPage.Section>
                      )}
                      {facilityNames.length > 0 && (
                        <DetailPage.Section title="공용 시설" aria-label="공용 시설">
                          <ul className="body flex flex-wrap gap-2">
                            {facilityNames.map((name, index) => (
                              <li
                                key={`${name}-${String(index)}`}
                                className="rounded-full border px-3 py-1"
                              >
                                {name}
                              </li>
                            ))}
                          </ul>
                        </DetailPage.Section>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {selectedTab === 'integrations' && (
                <div
                  id="property-detail-panel-integrations"
                  role="tabpanel"
                  className="flex scroll-mt-6 flex-col gap-6"
                  aria-labelledby="property-detail-tab-integrations"
                  tabIndex={0}
                >
                  <ProviderIntegrationStateSection
                    data={providerIntegrationState.data}
                    isFetching={providerIntegrationState.isFetching}
                    isError={providerIntegrationState.isError}
                    error={providerIntegrationState.error}
                    onRefresh={() => void providerIntegrationState.refetch()}
                  />
                  <ProviderLiveConnectionSection
                    key={`${propertyId}:${String(session.generation)}:${providerBinding.environment}:${String(providerBinding.branchId)}:${String(providerBinding.version)}`}
                    onRead={(signal) =>
                      readCurrentProviderLiveConnection(
                        runtime,
                        session.generation,
                        propertyId,
                        signal,
                        isProviderContextCurrent,
                      )
                    }
                    isCurrent={isProviderContextCurrent}
                  />
                  <ProviderLinkedDataSection
                    key={providerLinkedDataScopeKey}
                    scopeKey={providerLinkedDataScopeKey}
                    onRead={(signal, page) =>
                      readCurrentProviderLinkedData(
                        runtime,
                        session.generation,
                        propertyId,
                        signal,
                        page,
                        isProviderContextCurrent,
                      )
                    }
                  />
                </div>
              )}
              {selectedTab === 'web-preview' && (
                <div
                  id="property-detail-panel-web-preview"
                  role="tabpanel"
                  className="flex scroll-mt-6 flex-col gap-6"
                  aria-labelledby="property-detail-tab-web-preview"
                  tabIndex={0}
                >
                  <>
                    {propertyDetail.data && (
                      <LegacyPresentationSection
                        runtime={runtime}
                        catalog={detail.data ?? propertyDetail.data}
                      />
                    )}
                  </>
                  <WebsitePublicationSection
                    data={websitePublication.data}
                    isFetching={websitePublication.isFetching}
                    isError={websitePublication.isError}
                    error={websitePublication.error}
                    onRefresh={() => void websitePublication.refetch()}
                  />
                  <RecommendationWeightSection
                    propertyName={property.name}
                    propertyId={propertyId}
                    ownerScopeKey={`${recommendationScopeKey}:${String(recommendationEpoch)}`}
                    runtime={runtime}
                    generation={session.generation}
                    authorization={session.authorization}
                    isCurrent={isRecommendationScopeCurrent}
                    ownerState={recommendationOwnerState}
                    setOwnerState={setRecommendationOwnerState}
                  />
                </div>
              )}
              <div
                id="property-detail-panel-space"
                role="tabpanel"
                className="scroll-mt-6"
                aria-labelledby="property-detail-tab-space"
                tabIndex={0}
                hidden={selectedTab !== 'space'}
              >
                <div>{roomCatalogContent}</div>
                {session.authorization && canChangeApplicationSetting(session.authorization) && (
                  <details className="mt-4 rounded-md border p-4">
                    <summary className="label focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none">
                      신청 허용 설정 변경
                    </summary>
                    <div className="mt-4">
                      <ApplicationSettingForm
                        key={propertyId}
                        property={{
                          id: property.property_id,
                          name: property.name,
                          enabled: property.application_enabled,
                          version: property.version,
                        }}
                        onSave={(draft) => applicationSettingMutation.mutateAsync(draft)}
                        canSubmit={!propertyDetail.isError}
                      />
                    </div>
                  </details>
                )}
              </div>
              {selectedTab === 'media' && (
                <div
                  id="property-detail-panel-media"
                  role="tabpanel"
                  className="scroll-mt-6"
                  aria-labelledby="property-detail-tab-media"
                  tabIndex={0}
                >
                  <PropertyMediaSection
                    runtime={runtime}
                    generation={session.generation}
                    propertyId={propertyId}
                    media={state.media}
                    catalogs={[propertyDetail.data, detail.data]}
                    isCurrent={isPropertyCurrent}
                    onChange={onMediaChange}
                  />
                </div>
              )}
            </>
          ) : (
            <PropertyDetailSkeleton />
          )}
        </DetailPage.Body>
      </DetailPage>
    </Tabs>
  );
}
